import { ColumnDefinition } from "react-tabulator";

export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const formatDate = (date: Date | string): string => {
  let d;

  if (date instanceof Date) {
    d = date;
  } else {
    d = new Date(date);
  }

  return `${d.toLocaleDateString()} - ${d.toLocaleTimeString()}`;
};

export function formatPhoneNumber(phoneNumber: string | undefined) {
  if (!phoneNumber || typeof phoneNumber !== "string") return "";
  // Remove any non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Format based on number of digits
  if (cleaned.length <= 3) {
    return `(${cleaned}`;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10,
    )}`;
  }
}

export function unformatPhoneNumber(formattedPhoneNumber: string | undefined) {
  if (!formattedPhoneNumber) return "";
  // Remove all non-digit characters
  return formattedPhoneNumber.replace(/\D/g, "");
}

export const formatModifiedBy = (user: {
  modified_by: string | null;
  web_login: string | null;
}): string => {
  if (!user?.modified_by) return "";
  const parts = user.modified_by.split("@");
  if (parts.length > 0) {
    if (parts[0] === "psali") return user?.web_login || "Web Portal";
    if (parts[0] === "batch") return "SOI File";
  }
  return "INdigital Admin";
};

export const isBrowser = (): boolean => typeof window !== "undefined";

export const TabulatorOptions = ({
  layout = "fitColumns",
  pageSize = 10,
  columns,
  id,
  pagination = true,
  placeholder,
}: {
  layout?: string;
  pageSize?: number;
  pagination?: boolean;
  columns: ColumnDefinition[];
  id: string;
  placeholder?: string | null;
}): any => {
  return {
    pagination: pagination,
    layout: layout,
    paginationSize: pageSize,
    paginationSizeSelector: [10, 25, 50, 100],
    placeholder:
      placeholder === ""
        ? `<calcite-loader id=${id} scale="s"></calcite-loader>`
        : placeholder,
    columns: columns || [],
  };
};

const eventTypes = [
  "keypress",
  "mousemove",
  "mousedown",
  "scroll",
  "touchmove",
  "pointermove",
];
export const addEventListeners = (listener) => {
  eventTypes.forEach((type) => {
    window.addEventListener(type, listener, false);
  });
};
export const removeEventListeners = (listener) => {
  if (listener) {
    eventTypes.forEach((type) => {
      window.removeEventListener(type, listener, false);
    });
  }
};

export const sideFormatter = (type: number | undefined): string => {
  const values = {
    1: "O",
    2: "E",
    3: "B",
  };
  if (type) return values[type];
  else return "";
};

export const fieldsEqual = (field1, field2) => {
  if (field1) field1 = field1.trim();
  if (field2) field2 = field2.trim();
  if (field1 === field2) {
    return true;
  } else if (
    (field1 === "" && field2 === null) ||
    (field1 === null && field2 === "")
  ) {
    // if one field is an empty string and the other is null, treat as equal
    return true;
  } else {
    return false;
  }
};

// Update text in footer to show place in table
export const updateFooterText = (
  currentPage,
  normalPageSize,
  currentPageSize,
  count,
) => {
  var element = document.getElementById("record-count");
  if (element === null) return;
  if (currentPageSize > 0) {
    var index = (currentPage - 1) * normalPageSize + 1;
    var lastShown = index - 1 + currentPageSize;
    element.innerText =
      "Showing " + index + " to " + lastShown + " of " + count + " records";
  } else {
    element.innerText = "";
  }
};

export const cpnFormatter = (cell) => {
  var cpn = cell.getValue();
  if (!cpn) return "";
  var match = cpn.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return cpn;
};

export function formatHouseNumber(record, houseNumber) {
  let formatted = houseNumber || "";
  if (record.HNP && houseNumber) {
    formatted = record.HNP + formatted;
  }
  if (record.HNE && houseNumber) {
    formatted = formatted + record.HNE;
  }
  return formatted;
}

export function formatZipCode(zipCode: string | undefined) {
  if (!zipCode) return zipCode;

  return zipCode.split("-")[0];
}

export const hnoFormatter = (cell) => {
  var hno = cell.getValue() || "";
  var data = cell.getRow().getData();
  var hnp = data.HNP ? data.HNP : "";
  var hne = data.HNE ? data.HNE : "";
  return hnp + hno + hne;
};

export const fileFormatter = (cell) => {
  var filename = cell.getValue();
  if (filename) {
    var file = "";
    if (filename !== "" && filename.indexOf("--") !== 0) {
      var index = filename.indexOf("--") + 2;
      file = filename.substring(index);
    } else {
      file = filename;
    }
    return file;
  } else {
    return "";
  }
};

export function formatSide(side) {
  const formatterParams = {
    1: "O",
    2: "E",
    3: "B",
  };
  return formatterParams[side] || side;
}

export const translateToLocalDate = (value) => {
  if (!value || value === "0000-00-00 00:00:00") return null;
  var local = new Date(value.replace(/-/g, "/")); // Support Safari
  var year = local.getFullYear();
  var month = local.getMonth() + 1;
  var date = local.getDate();
  var hrs = local.getHours();
  var min = local.getMinutes();
  var sec = local.getSeconds();

  const monthStr = month < 10 ? "0" + month : String(month);
  const dateStr = date < 10 ? "0" + date : String(date);
  const hrsStr = hrs < 10 ? "0" + hrs : String(hrs);
  const minStr = min < 10 ? "0" + min : String(min);
  const secStr = sec < 10 ? "0" + sec : String(sec);

  var fullDate =
    year +
    "-" +
    monthStr +
    "-" +
    dateStr +
    " " +
    hrsStr +
    ":" +
    minStr +
    ":" +
    secStr;
  return fullDate;
};
